import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Alias for `subtitle`. */
  description?: string;
  /** Multiple action buttons / elements. */
  actions?: ReactNode;
  /** Single action element. Alias for `actions` — kept for backward compatibility. */
  action?: ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, description, actions, action, className }: PageHeaderProps) {
  const actionSlot = actions ?? action;
  const body = subtitle ?? description;
  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="mt-1 hidden h-8 w-1.5 flex-shrink-0 rounded-full bg-gradient-to-b from-primary to-primary/40 sm:block"
        />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.7rem]">{title}</h1>
          {body && (
            <p className="mt-1 text-sm text-muted-foreground">{body}</p>
          )}
        </div>
      </div>
      {actionSlot && (
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">{actionSlot}</div>
      )}
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  actions?: ReactNode;
  className?: string;
}

export function SectionHeader({ title, actions, className }: SectionHeaderProps) {
  return (
    <div className={cn('flex items-center justify-between px-4 py-3 border-b border-border', className)}>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
